/**
 * Header v9 - Interactive functionality
 * Handles scroll behavior, mobile drawer, search modal, and accessibility
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    SCROLL_THRESHOLD: 24,
    TRANSITION_DURATION: 250,
    SEARCH_DEBOUNCE: 300,
    FOCUS_TRAP_SELECTOR: '[tabindex]:not([tabindex="-1"]), a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])'
  };

  // State management
  const state = {
    header: null,
    isOverlayMode: false,
    scrollTicking: false,
    lastScrollTop: 0,
    searchTimeout: null,
    isSearchOpen: false,
    isDrawerOpen: false,
    focusableElements: [],
    firstFocusableElement: null,
    lastFocusableElement: null
  };

  // Utility functions
  const utils = {
    // Throttle function for performance
    throttle: (func, limit) => {
      let inThrottle;
      return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    // Debounce function for search
    debounce: (func, wait) => {
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(state.searchTimeout);
          func(...args);
        };
        clearTimeout(state.searchTimeout);
        state.searchTimeout = setTimeout(later, wait);
      };
    },

    // Get focusable elements for trap
    getFocusableElements: (container) => {
      return Array.from(container.querySelectorAll(CONFIG.FOCUS_TRAP_SELECTOR));
    },

    // Trap focus within element
    trapFocus: (element) => {
      state.focusableElements = utils.getFocusableElements(element);
      state.firstFocusableElement = state.focusableElements[0];
      state.lastFocusableElement = state.focusableElements[state.focusableElements.length - 1];

      element.addEventListener('keydown', utils.handleFocusTrap);
    },

    // Handle focus trap navigation
    handleFocusTrap: (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === state.firstFocusableElement) {
          state.lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === state.lastFocusableElement) {
          state.firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    },

    // Remove focus trap
    removeFocusTrap: () => {
      if (state.focusableElements.length > 0) {
        state.focusableElements.forEach(el => el.removeEventListener('keydown', utils.handleFocusTrap));
      }
      state.focusableElements = [];
      state.firstFocusableElement = null;
      state.lastFocusableElement = null;
    }
  };

  // Header scroll behavior
  const headerScroll = {
    init: () => {
      if (!state.header) return;

      state.isOverlayMode = state.header.classList.contains('header-v9--overlay');

      // Use IntersectionObserver for better performance
      if ('IntersectionObserver' in window && state.isOverlayMode) {
        headerScroll.initIntersectionObserver();
      } else {
        // Fallback to scroll listener
        headerScroll.initScrollListener();
      }
    },

    initIntersectionObserver: () => {
      const sentinel = document.createElement('div');
      sentinel.style.height = '1px';
      sentinel.style.position = 'absolute';
      sentinel.style.top = '0';
      sentinel.style.left = '0';
      document.body.insertBefore(sentinel, document.body.firstChild);

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            state.header.classList.remove('is-solid');
          } else {
            state.header.classList.add('is-solid');
          }
        });
      }, {
        rootMargin: `${CONFIG.SCROLL_THRESHOLD}px 0px 0px 0px`
      });

      observer.observe(sentinel);
    },

    initScrollListener: () => {
      const handleScroll = utils.throttle(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (Math.abs(scrollTop - state.lastScrollTop) < 2) return;

        state.lastScrollTop = scrollTop;

        if (state.isOverlayMode) {
          // Try to detect hero/video section
          const hero = headerScroll.detectHeroSection();
          if (hero) {
            const heroBottom = hero.offsetTop + hero.offsetHeight;
            const triggerPoint = Math.max(heroBottom - 100, hero.offsetHeight * 0.8);

            if (scrollTop > triggerPoint) {
              state.header.classList.add('is-solid');
            } else {
              state.header.classList.remove('is-solid');
            }
          } else if (scrollTop > CONFIG.SCROLL_THRESHOLD) {
            state.header.classList.add('is-solid');
          } else {
            state.header.classList.remove('is-solid');
          }
        } else if (scrollTop > CONFIG.SCROLL_THRESHOLD) {
          state.header.classList.add('is-solid');
        } else {
          state.header.classList.remove('is-solid');
        }
      }, 16);

      window.addEventListener('scroll', handleScroll, { passive: true });
    },

    detectHeroSection: () => {
      const selectors = [
        '.video-hero-section',
        '.hero-section',
        '[data-hero]',
        '.custom-html-section .video-hero-section',
        '.custom-html-content .video-hero-section',
        'video[autoplay]',
        '.hero-video'
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) return element;
      }
      return null;
    }
  };

  // Mobile drawer functionality
  const mobileDrawer = {
    init: () => {
      const toggleBtn = document.getElementById('hdv9-toggle');
      const drawer = document.getElementById('hdv9-mobile');

      if (!toggleBtn || !drawer) return;

      mobileDrawer.attachEvents(toggleBtn, drawer);
    },

    attachEvents: (toggleBtn, drawer) => {
      // Toggle button
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        mobileDrawer.toggle(drawer, toggleBtn);
      });

      // Close events
      drawer.addEventListener('click', (e) => {
        if (e.target.closest('.js-hdv9-close') || e.target.classList.contains('header-v9__drawer-overlay')) {
          mobileDrawer.close(drawer, toggleBtn);
        }
      });

      // Keyboard events
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && state.isDrawerOpen) {
          mobileDrawer.close(drawer, toggleBtn);
        }
      });
    },

    toggle: (drawer, toggleBtn) => {
      if (state.isDrawerOpen) {
        mobileDrawer.close(drawer, toggleBtn);
      } else {
        mobileDrawer.open(drawer, toggleBtn);
      }
    },

    open: (drawer, toggleBtn) => {
      state.isDrawerOpen = true;
      drawer.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('hdv9-lock');

      // Force header to solid state when drawer is open
      state.header.classList.add('is-solid');

      // Focus management
      setTimeout(() => {
        const firstFocusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
          firstFocusable.focus();
        }
        utils.trapFocus(drawer);
      }, CONFIG.TRANSITION_DURATION);
    },

    close: (drawer, toggleBtn) => {
      state.isDrawerOpen = false;
      drawer.setAttribute('aria-hidden', 'true');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('hdv9-lock');

      utils.removeFocusTrap();
      toggleBtn.focus();
    }
  };

  // Search modal functionality
  const hdv9SearchModal = {
    init: () => {
      const searchBtns = document.querySelectorAll('.header-v9__search-btn');
      const searchModalEl = document.getElementById('hdv9-search');

      if (!searchModalEl || searchBtns.length === 0) return;

      searchBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          hdv9SearchModal.open();
        });
      });

      hdv9SearchModal.attachCloseEvents();
      hdv9SearchModal.setupPredictiveSearch();
    },

    open: () => {
      state.isSearchOpen = true;
      const modal = document.getElementById('hdv9-search');
      const input = modal.querySelector('.header-v9__search-input');

      modal.setAttribute('aria-hidden', 'false');

      // Force header to solid state when search is open
      state.header.classList.add('is-solid');

      // Focus management
      setTimeout(() => {
        if (input) {
          input.focus();
        }
        utils.trapFocus(modal);
      }, CONFIG.TRANSITION_DURATION);
    },

    close: () => {
      state.isSearchOpen = false;
      const modal = document.getElementById('hdv9-search');

      modal.setAttribute('aria-hidden', 'true');
      utils.removeFocusTrap();

      // Return focus to search button
      const searchBtn = document.querySelector('.header-v9__search-btn');
      if (searchBtn) {
        searchBtn.focus();
      }
    },

    attachCloseEvents: () => {
      const modal = document.getElementById('hdv9-search');

      modal.addEventListener('click', (e) => {
        if (e.target.closest('.js-hdv9-search-close') || e.target.classList.contains('header-v9__search-overlay')) {
          hdv9SearchModal.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && state.isSearchOpen) {
          hdv9SearchModal.close();
        }
      });
    },

    setupPredictiveSearch: () => {
      const input = document.querySelector('.header-v9__search-input');
      const resultsContainer = document.querySelector('.header-v9__search-results');

      if (!input || !resultsContainer) return;

      const performSearch = utils.debounce(async (query) => {
        if (query.length < 2) {
          resultsContainer.innerHTML = '';
          return;
        }

        try {
          const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}`);
          const data = await response.json();

          if (data.resources.results.products) {
            hdv9SearchModal.displayResults(data.resources.results.products, resultsContainer);
          }
        } catch (error) {
          console.warn('Predictive search failed:', error);
        }
      }, CONFIG.SEARCH_DEBOUNCE);

      input.addEventListener('input', (e) => {
        performSearch(e.target.value);
      });
    },

    displayResults: (products, container) => {
      if (!products.length) {
        container.innerHTML = '<p class="text-center text-muted">No products found</p>';
        return;
      }

      const results = products.slice(0, 5).map(product => `
        <div class="search-result-item">
          <a href="${product.url}" class="search-result-link">
            <img src="${product.featured_image.url}" alt="${product.featured_image.alt}" width="40" height="40">
            <div class="search-result-info">
              <h4>${product.title}</h4>
              <p>${product.price}</p>
            </div>
          </a>
        </div>
      `).join('');

      container.innerHTML = `<div class="search-results-list">${results}</div>`;
    }
  };

  // Keyboard shortcuts
  const keyboardShortcuts = {
    init: () => {
      document.addEventListener('keydown', (e) => {
        // Forward slash to open search
        if (e.key === '/' && !e.target.matches('input, textarea, [contenteditable]')) {
          e.preventDefault();
          const searchBtn = document.querySelector('.header-v9__search-btn');
          if (searchBtn) {
            searchBtn.click();
          }
        }
      });
    }
  };

  // Cart functionality
  const cartIntegration = {
    init: () => {
      // Update cart count when cart changes
      document.addEventListener('cart:updated', (e) => {
        const countElements = document.querySelectorAll('.header-v9__cart-count');
        const count = e.detail.cart.item_count;

        countElements.forEach(el => {
          el.textContent = count > 99 ? '99+' : count;
        });
      });
    }
  };

  // Accessibility enhancements
  const accessibility = {
    init: () => {
      // Add skip link
      accessibility.addSkipLink();

      // Handle reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        state.header.style.transition = 'none';
      }

      // High contrast mode support
      if (window.matchMedia('(prefers-contrast: high)').matches) {
        document.documentElement.classList.add('high-contrast');
      }
    },

    addSkipLink: () => {
      const skipLink = document.createElement('a');
      skipLink.href = '#MainContent';
      skipLink.className = 'header-v9-skip-link';
      skipLink.textContent = 'Skip to main content';
      state.header.insertBefore(skipLink, state.header.firstChild);
    }
  };

  // Performance monitoring
  const performance = {
    init: () => {
      // Monitor loading performance
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'measure' && entry.name.includes('header-v9')) {
              console.log(`Header v9 ${entry.name}: ${entry.duration}ms`);
            }
          }
        });

        observer.observe({ entryTypes: ['measure'] });
      }
    }
  };

  // Initialize everything when DOM is ready
  const init = () => {
    state.header = document.querySelector('.header-v9');
    if (!state.header) return;

    // Apply fullscreen menu setting if enabled
    const fullscreenMenuEnabled = state.header.hasAttribute('data-fullscreen-menu');
    if (fullscreenMenuEnabled) {
      document.addEventListener('DOMContentLoaded', () => {
        const drawerPanel = document.querySelector('.header-v9__drawer-panel');
        if (drawerPanel) {
          drawerPanel.style.width = '100vw';
          drawerPanel.style.maxWidth = 'none';
          drawerPanel.style.height = '100vh';
          drawerPanel.style.paddingTop = '88px'; // Account for header height
        }
      });
    }

    // Fix click blocking issue - remove conflicting overlays
    const conflictingOverlays = document.querySelectorAll('.js-bg.bg-minicart, .bg-minicart');
    conflictingOverlays.forEach(overlay => {
      overlay.style.pointerEvents = 'none';
    });

    // Initialize modules
    headerScroll.init();
    mobileDrawer.init();
    hdv9SearchModal.init();
    keyboardShortcuts.init();
    cartIntegration.init();
    accessibility.init();

    // Mark as loaded
    setTimeout(() => {
      state.header.classList.add('is-loaded');
    }, 100);

    // Performance monitoring
    if (window.performance && window.performance.mark) {
      window.performance.mark('header-v9-ready');
    }
  };

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Fullscreen mobile menu handlers
  document.addEventListener('DOMContentLoaded', () => {
    const toggle = document.getElementById('hdv9-toggle');
    const closeBtns = document.querySelectorAll('.js-hdv9-close');
    const body = document.body;

    if (toggle) {
      toggle.addEventListener('click', () => body.classList.add('menu-open'));
    }
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => body.classList.remove('menu-open'));
    });
  });

  // Expose public API for external use
  window.HeaderV9 = {
    openSearch: hdv9SearchModal.open,
    closeSearch: hdv9SearchModal.close,
    openDrawer: () => mobileDrawer.open(
      document.getElementById('hdv9-mobile'),
      document.getElementById('hdv9-toggle')
    ),
    closeDrawer: () => mobileDrawer.close(
      document.getElementById('hdv9-mobile'),
      document.getElementById('hdv9-toggle')
    )
  };

})();
